import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { toast } from "react-toastify"
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { memoize } from "lodash"
import { dayjs, getCheckoutNoteValue, timeFormat } from "src/app/utils"
import {
  otherCheckoutConfigSelector,
  setCart,
  setCheckoutOption,
} from "src/features/checkout/checkoutSlice"
import {
  DELIVERY_OPTIONS,
  SPECIAL_EVENTS,
  SPECIAL_EVENTS_DATA,
} from "src/app/constants"
import BackAndNextButton from "./common/BackAndNextButton"
import { useLazyGetZipScheduleQuery } from "src/features/checkout/checkoutApi"
import { useCartNoteUpdateMutation } from "src/features/checkout/storefrontCartApi"
import HawaiiGet from "./common/HawaiiGet"
import analytics from "src/app/analytics"

const specialEvents = {
  [SPECIAL_EVENTS.HANALE_ISLAND_SAKE]: "Hanale by Island Sake",
  [SPECIAL_EVENTS.INSPIRE_YACHT]: "Inspire Yacht",
}

const UPDATE_DATE_INTERVAL_MS = 10000
const dateFormat = "YYYY-MM-DD"
const dateTimeFormat = `${dateFormat} hh:mm:ss`
const now = dayjs()
const formattedNow = now.format(dateFormat)
const tomorrow = now.add(1, "day").startOf("day")
const formattedTomorrow = tomorrow.format(dateFormat)

export default function Schedule() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down("md"))
  const cart = useSelector((state) => state.checkout.cart)
  const customer = useSelector((state) => state.checkout.customer)
  const options = useSelector((state) => state.checkout.options)
  const { deliveryOption, specialEvent } = options
  const [minDate, setMinDate] = useState(now)
  const {
    hasNextDayProducts,
    customLabelProducts,
    customStartDateProducts,
    checkoutVendorSchedules,
  } = useSelector(otherCheckoutConfigSelector)
  const [scheduleData, setScheduleData] = useState(null)
  const [serverDate, setServerDate] = useState(now)
  const [formattedServerDate, setFormattedServerDate] = useState(formattedNow)
  const [deliveryDate, setDeliveryDate] = useState(null)
  const [slots, setSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState("")
  const [selectedSlotValue, setSelectedSlotValue] = useState("slot_0")
  const [texts, setTexts] = useState({
    title: "Schedule",
    subtitle: "",
  })

  const { control, handleSubmit } = useForm({
    defaultValues: {
      email: customer.email || cart.buyerIdentity?.email || "",
    },
  })

  const [getZipSchedule, { data: zipSchedule, isLoading: zipScheduleLoading }] =
    useLazyGetZipScheduleQuery()

  const [
    cartNoteUpdate,
    {
      data: cartNoteUpdateData,
      isLoading: cartNoteUpdateLoading,
      isError: cartNoteUpdateError,
    },
  ] = useCartNoteUpdateMutation()

  const onSubmit = () => {
    if (deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING) {
      navigate("../payment")
      return
    }

    const deliverySchedule = [deliveryDate.format(dateFormat), selectedSlot]
      .filter(Boolean)
      .join(" ")
    const checkoutNote = getCheckoutNoteValue(
      cart.note || "",
      deliverySchedule,
    )

    analytics.track("select_delivery_schedule", {
      cart_id: cart.id,
      schedule: deliverySchedule,
    })

    cartNoteUpdate({
      cartId: cart.id,
      note: checkoutNote,
    })
  }

  const dateHasSlot = memoize((calDay, formattedDate) => {
    // hanale delivery doesn't have a slot, so return true
    if (
      [
        SPECIAL_EVENTS.HANALE_ISLAND_SAKE,
        SPECIAL_EVENTS.INSPIRE_YACHT,
      ].includes(specialEvent)
    ) {
      return true
    }

    const foundSlot = scheduleData?.slots.find((slot) => {
      let hasSlot = false

      if (slot.type == "weekday") {
        hasSlot = slot.value.includes(calDay)
        if (hasSlot) {
          hasSlot = !slot.remove_dates.includes(formattedDate)
        }
      } else if (slot.type == "day") {
        hasSlot = slot.value == formattedDate
      }

      if (hasSlot && formattedDate == formattedServerDate) {
        const timeSlotStartTime = dayjs(
          `${formattedDate} ${slot.time_start}`,
          dateTimeFormat,
        )
        let cutOffTime
        if (slot.cutoff_time) {
          cutOffTime = dayjs(
            `${formattedDate} ${slot.cutoff_time}`,
            dateTimeFormat,
          )
        } else {
          // If no cutoff time, set cutoff time 15 minutes before start time.
          cutOffTime = timeSlotStartTime.subtract(15, "minutes")
        }
        if (serverDate > cutOffTime) {
          hasSlot = false
        }
      }

      return hasSlot
    })

    return foundSlot ? true : false
  })

  const shouldDisableDate = memoize((date) => {
    date = date.startOf("day")
    const formattedDate = date.format(dateFormat)
    const day = date.day()

    // Disable dates before min date.
    if (date.isBefore(minDate.startOf("day"))) {
      return true
    }

    if (hasNextDayProducts) {
      // Any order placed before 4pm on Day 1, would be available for delivery on Day 2
      if (formattedNow === formattedDate) return true

      // Any order placed after 4pm on Day 1 would be available for delivery on Day 3
      if (
        serverDate.isAfter(date.hour(16)) &&
        formattedTomorrow === formattedDate
      ) {
        return true
      }
    }

    if (checkoutVendorSchedules.length) {
      for (const schedule of checkoutVendorSchedules) {
        if (!schedule.day_of_the_week?.length) continue

        // If vendor is closed or doesn't have schedule on this date.
        if (!schedule.day_of_the_week.includes(day)) {
          return true
        }
      }
    }

    if (
      scheduleData.exceptionDates &&
      scheduleData.exceptionDates.includes(formattedDate)
    ) {
      return true
    }

    if (
      scheduleData.availableDates &&
      scheduleData.availableDates.includes(formattedDate) &&
      dateHasSlot(day, formattedDate)
    ) {
      return false
    }

    if (
      scheduleData.availableWeekdays &&
      scheduleData.availableWeekdays.includes(day) &&
      dateHasSlot(day, formattedDate)
    ) {
      return false
    }

    return true
  })

  const acceptDate = (date) => {
    const selectedDate = date?.format(dateFormat)
    let checked = true
    let hasSlot = false
    let timeRange = ""
    let newSlots = []

    if (scheduleData?.slots) {
      scheduleData.slots.map((slot) => {
        hasSlot = false
        if (slot.type == "weekday") {
          hasSlot = slot.value.includes(date?.day())
          if (hasSlot) {
            hasSlot = !slot.remove_dates.includes(selectedDate)
          }
        } else if (slot.type == "day") {
          hasSlot = slot.value == selectedDate
        }

        if (hasSlot && selectedDate == formattedServerDate) {
          const timeSlotStartTime = dayjs(
            `${selectedDate} ${slot.time_start}`,
            dateTimeFormat,
          )
          let cutOffTime
          if (slot.cutoff_time) {
            cutOffTime = dayjs(
              `${selectedDate} ${slot.cutoff_time}`,
              dateTimeFormat,
            )
          } else {
            // If no cutoff time, set cutoff time 15 minutes before start time.
            cutOffTime = timeSlotStartTime.subtract(15, "minutes")
          }
          if (serverDate > cutOffTime) {
            hasSlot = false
          }
        }

        if (hasSlot) {
          // overrides
          if (slot.override_dates[selectedDate]) {
            slot.override_dates[selectedDate].forEach((s) => {
              timeRange = timeFormat(s[0]) + " - " + timeFormat(s[1])
              newSlots.push({
                val: timeRange,
                address: slot.address ?? "",
                checked,
              })

              if (checked) {
                setSelectedSlot(timeRange)
                dispatch(
                  setCheckoutOption({
                    pickupLocation: slot?.location ?? null,
                  }),
                )
              }
            })
          } else {
            timeRange =
              timeFormat(slot.time_start) + " - " + timeFormat(slot.time_end)
            newSlots.push({
              val: timeRange,
              address: slot.address ?? "",
              checked,
            })

            if (checked) {
              setSelectedSlot(timeRange)
              dispatch(
                setCheckoutOption({
                  pickupLocation: slot?.location ?? null,
                }),
              )
            }
          }

          checked = false
        }
      })
    }

    setSlots(newSlots)
  }

  const handleSlotChange = (event) => {
    setSelectedSlotValue(event.target.value)
    setSelectedSlot(event.target.labels[0].innerText)
  }

  function handleBackButtonClickEvent() {
    if (location.state.previousOptions) {
      dispatch(setCheckoutOption(location.state.previousOptions))
    }
  }

  useEffect(() => {
    window.removeEventListener("popstate", handleBackButtonClickEvent)
    window.addEventListener("popstate", handleBackButtonClickEvent)
  }, [location])

  useEffect(() => {
    if (!deliveryDate || !scheduleData) return

    // Make sure initial deliveryDate is not disabled.
    if (shouldDisableDate(deliveryDate)) {
      setDeliveryDate(deliveryDate.add("1", "day"))
      return
    }

    acceptDate(deliveryDate)
  }, [deliveryDate, scheduleData])

  useEffect(() => {
    if (!deliveryOption) {
      navigate("../options")
      return
    }

    if (
      deliveryOption !== DELIVERY_OPTIONS.LOCAL_PICKUP &&
      (!cart.delivery.addresses[0]?.address?.address1 || !cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.handle)
    ) {
      console.log('No delivery option set', cart)
      navigate(-1)
      return
    }

    if (deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY) {
      if (specialEvents[specialEvent]) {
        setTexts({
          ...texts,
          title: "Schedule",
          subtitle: `Please select the day of your reservation at ${specialEvents[specialEvent]}.`,
        })
        setScheduleData(SPECIAL_EVENTS_DATA[specialEvent].schedule)
      } else {
        setTexts({ ...texts, title: "Delivery Schedule", subtitle: "" })
        getZipSchedule({ zip: cart.delivery.addresses[0]?.address?.zip, isAsrc: false })
      }
    } else if (deliveryOption === DELIVERY_OPTIONS.LOCAL_PICKUP) {
      setTexts({ ...texts, title: "Pickup Schedule", subtitle: "" })
      setScheduleData(JSON.parse(import.meta.env.VITE_PICKUP_SCHEDULE))
    }

    document.title = "Schedule - Kakaako Wine - Checkout"

    analytics.track("page_view", {
      cart_id: cart.id,
      page_title: "Information",
      page_location: window.location.href,
    })

    if (customLabelProducts.length > 0) {
      toast(
        "You have a custom item in your cart that requires some extra time for delivery. If you’d like an earlier delivery date for the rest of your order, please order the custom item in a separate cart. Mahalo!",
        {
          position: "top-center",
          type: "warning",
          autoClose: false,
          closeOnClick: false,
          style: {
            marginTop: "125px",
          },
        },
      )
    }

    return () => toast.dismiss()
  }, [deliveryOption, specialEvent])

  useEffect(() => {
    if (scheduleData?.now) {
      let serverDate = dayjs(scheduleData.now, dateTimeFormat)
      setServerDate(serverDate)
      setFormattedServerDate(serverDate.format(dateFormat))

      const interval = setInterval(() => {
        serverDate = serverDate.add(UPDATE_DATE_INTERVAL_MS / 1000, "second")
        setServerDate(serverDate)
        setFormattedServerDate(serverDate.format(dateFormat))

        if (serverDate.isAfter(minDate)) {
          setMinDate(serverDate)
        }
      }, UPDATE_DATE_INTERVAL_MS)

      return () => clearInterval(interval)
    }
  }, [scheduleData])

  useEffect(() => {
    if (zipSchedule) setScheduleData(zipSchedule)
  }, [zipSchedule])

  useEffect(() => setSelectedSlotValue("slot_0"), [slots])

  useEffect(() => {
    if (deliveryDate && deliveryDate.isAfter(serverDate)) return

    let maxMinDate = minDate

    if (customStartDateProducts.length) {
      const customStartDates = customStartDateProducts.map(
        (i) => new Date(i.delivery_start_date),
      )
      maxMinDate = dayjs(customStartDates.sort((a, b) => b - a)[0])
    }

    if (maxMinDate.isAfter(minDate)) {
      setMinDate(maxMinDate)
    }

    if (maxMinDate.isAfter(serverDate)) {
      setDeliveryDate(maxMinDate)
    } else {
      setDeliveryDate(serverDate)
    }
  }, [minDate, serverDate, customStartDateProducts])

  useEffect(() => {
    if (cartNoteUpdateData?.cartNoteUpdate?.cart) {
      dispatch(
        setCart(
          cartNoteUpdateData.cartNoteUpdate.cart,
        ),
      )
      navigate(
        deliveryOption === DELIVERY_OPTIONS.LOCAL_PICKUP
          ? "../information"
          : "../payment",
      )
    }

    if (cartNoteUpdateError) {
      toast.error(
        "An error occured while trying to set the schedule. Please try again in a while.",
      )
    }
  }, [cartNoteUpdateData, cartNoteUpdateError])

  if (
    !scheduleData &&
    ![
      DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING,
      DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
    ].includes(deliveryOption)
  ) {
    return (
      <Box display="flex" flex={1} justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography component="h2" variant="h5">
        {texts.title}
      </Typography>
      {[
        DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING,
        DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
      ].includes(deliveryOption) ? (
        <Box paddingY={12} textAlign="center">
          <Typography variant="h3">
            {deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING
              ? "Neighbor Island"
              : "Mainland"}{" "}
            Shipping
          </Typography>
          <Typography variant="h5" color="grey">
            Estimated transit time: (
            {deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING
              ? "1 business day"
              : "3-5 business days"}
            )
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="subtitle1" mb={2}>
            {texts.subtitle}
          </Typography>

          {scheduleData?.availableDates.length === 0 &&
          scheduleData?.availableWeekdays.length === 0 ? (
            <Box component={Paper}>
              <Alert severity="info">
                Our Kaka&#39;ako Wine Team will be in touch with you to schedule
                your delivery.
              </Alert>
            </Box>
          ) : (
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker
                  className="no-actionbar-datepicker"
                  orientation={downMd ? "portrait" : "portrait"}
                  openTo="day"
                  value={deliveryDate}
                  onChange={setDeliveryDate}
                  minDate={minDate.isAfter(serverDate) ? minDate : serverDate}
                  maxDate={serverDate.add(1, "year")}
                  shouldDisableDate={shouldDisableDate}
                  disableHighlightToday
                />
              </LocalizationProvider>

              {![
                SPECIAL_EVENTS.HANALE_ISLAND_SAKE,
                SPECIAL_EVENTS.INSPIRE_YACHT,
              ].includes(specialEvent) && (
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Please select your preferred time slot
                  </FormLabel>
                  {slots && (
                    <RadioGroup
                      aria-label="slot"
                      name="slot"
                      value={selectedSlotValue}
                      onChange={handleSlotChange}
                    >
                      {slots.map((slot, i) => (
                        <FormControlLabel
                          key={i}
                          value={`slot_${i}`}
                          control={<Radio data-address={slot.address} />}
                          label={slot.val}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </FormControl>
              )}
            </>
          )}
        </>
      )}

      <BackAndNextButton
        loading={cartNoteUpdateLoading}
        // backButtonLabel={`Return to ${
        //   deliveryOption === DELIVERY_OPTIONS.LOCAL_PICKUP
        //     ? "options"
        //     : deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY
        //     ? "delivery"
        //     : "shipping"
        // }`}
        backButtonPath={
          deliveryOption === DELIVERY_OPTIONS.LOCAL_PICKUP
            ? "../options"
            : deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY
            ? "../delivery"
            : "../shipping"
        }
        nextButtonLabel={`Continue to ${
          deliveryOption === DELIVERY_OPTIONS.LOCAL_PICKUP
            ? "information"
            : "payment"
        }`}
        nextButtonType="submit"
      />

      {deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY && !specialEvent && (
        <Box
          component={Paper}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          sx={{ flexDirection: { xs: "column", sm: "column", md: "row" } }}
        >
          <Typography variant="body2" gutterBottom>
            Not seeing dates and times that work with your schedule? <br />
            No problem! We offer warehouse pick up 7 days a week in Mapunapuna
            by the Airport.
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              const previousOptions = options
              dispatch(
                setCheckoutOption({
                  deliveryOption: DELIVERY_OPTIONS.LOCAL_PICKUP,
                  zip: null,
                  specialEvent: null,
                }),
              )
              navigate("../schedule", {
                state: {
                  previousOptions,
                },
              })
            }}
          >
            Schedule Warehouse Pickup
          </Button>
        </Box>
      )}

      <HawaiiGet />
    </form>
  )
}
